import api from './axios';
import {
  setBusinessACookies,
  setBusinessBCookies,
  clearBusinessCookies,
} from '@/lib/cookies';
import { useBusinessStore } from '@/store/businessStore';

interface LoginResponse {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  token: string;
}

interface LoginCredentials {
  user_email: string;
  user_password: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      try {
        const response = await this.loginBusinessA(credentials);
        return response;
      } catch (error) {
        console.error('Business-A login failed, trying Business-B:', error);
        try {
          const response = await this.loginBusinessB(credentials);
          return response;
        } catch (businessBError) {
          console.error('Business-B login also failed:', businessBError);
          throw new Error('All login attempts failed');
        }
      }
    } catch (error: any) {
      console.error('Authentication failed:', error.message);
      throw error;
    }
  },

  async loginBusinessA(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await fetch(
        'https://api.scriptlinkrx.com/authentication/business-a-login',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            user_email: credentials.user_email,
            user_password: credentials.user_password,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Business-A login failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.data?.business_a) {
        useBusinessStore.getState().setBusinessData(data.data.business_a);
      }

      let token = null;
      if (data.token) {
        token = data.token;
      } else if (data.data?.user_session?.auth_key) {
        token = data.data.user_session.auth_key;
      } else if (data.data?.auth_key) {
        token = data.data.auth_key;
      }

      if (!token) {
        throw new Error('No token found in response');
      }

      localStorage.setItem('token', token);
      localStorage.setItem('businessAToken', token);
      const user = {
        id: data.data?.user_session?.user_id || 'business-a-user',
        name: data.data?.business_a.business_name,
        email: data.data?.user_email,
        businessId: data.data?.business_a.id,
        role: 'business-a',
        prescriberProfile: data?.data?.user_prescriber_profile,
        has_signature: !!data?.data?.user_prescriber_signature,
      };
      localStorage.setItem('businessAUser', JSON.stringify(user));
      setBusinessACookies(
        token,
        user,
        new Date(data.data.user_session.expired_at.Time)
      );

      return {
        user,
        token,
      };
    } catch (error: any) {
      console.error('Business-A authentication failed:', error.message);
      throw error;
    }
  },

  async loginBusinessB(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      try {
        const response = await fetch(
          'https://api.scriptlinkrx.com/authentication/business-b-login',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              user_email: credentials.user_email,
              user_password: credentials.user_password,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();

          // Store business data in Zustand store
          if (data.data?.business_b) {
            useBusinessStore.getState().setBusinessData(data.data);
          }

          let token = null;
          if (data.token) {
            token = data.token;
          } else if (data.data?.user_session?.auth_key) {
            token = data.data.user_session.auth_key;
          } else if (data.data?.auth_key) {
            token = data.data.auth_key;
          }

          if (token) {
            localStorage.setItem('token', token);
            localStorage.setItem('businessBToken', token);

            const user = {
              id: data.data?.user_profile?.user_id || 'business-b-user',
              name: data.data?.business_b.business_name,
              email: data.data?.user_email,
              businessId: data.data?.business_b.id,
              role: 'business-b',
            };
            localStorage.setItem('businessBUser', JSON.stringify(user));

            // Use the new helper function to set cookies with proper expiration
            setBusinessBCookies(token, user, data);

            return {
              user,
              token,
            };
          }
        }
      } catch (error) {
        console.error('Direct business-b login failed:', error);
      }

      const payloads = [
        credentials,
        {
          email: credentials.user_email,
          password: credentials.user_password,
        },
        {
          user: {
            email: credentials.user_email,
            password: credentials.user_password,
          },
        },
      ];

      for (const payload of payloads) {
        try {
          const response = await api.post<any>(
            '/authentication/business-b-login',
            payload
          );

          if (response.data) {
            let token = null;
            if (response.data.token) {
              token = response.data.token;
            } else if (response.data.data?.user_session?.auth_key) {
              token = response.data.data.user_session.auth_key;
            } else if (response.data.data?.auth_key) {
              token = response.data.data.auth_key;
            }

            if (token) {
              localStorage.setItem('token', token);
              localStorage.setItem('businessBToken', token);

              const user = {
                id:
                  response.data.data?.user?.id ||
                  response.data.data?.id ||
                  'business-b-user',
                name:
                  response.data.data?.user?.name ||
                  response.data.data?.name ||
                  credentials.user_email,
                email:
                  response.data.data?.user?.email ||
                  response.data.data?.email ||
                  credentials.user_email,
                role: 'business-b',
              };
              setBusinessBCookies(token, user, response.data);

              return {
                user,
                token,
              };
            }
          }
        } catch (error) {
          console.error('Axios business-b login attempt failed:', error);
        }
      }

      throw new Error('Business-B login failed');
    } catch (error: any) {
      console.error('Business-B authentication failed:', error.message);
      throw error;
    }
  },

  isBusinessALoggedIn(): boolean {
    if (typeof window === 'undefined') return false;

    const token =
      localStorage.getItem('businessAToken') || localStorage.getItem('token');
    return !!token;
  },

  isBusinessBLoggedIn(): boolean {
    if (typeof window === 'undefined') return false;

    const token =
      localStorage.getItem('businessBToken') || localStorage.getItem('token');
    return !!token;
  },

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('businessAToken');
    localStorage.removeItem('businessBToken');
    localStorage.removeItem('businessAUser');
    localStorage.removeItem('businessBUser');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminUserId');

    // Clear Zustand store
    useBusinessStore.getState().clearBusinessData();

    clearBusinessCookies();
  },
};

export default authService;
